import { inject } from '@angular/core'
import { CanActivateFn, Router } from '@angular/router'
import { CourtSystemParams } from '@ftr/routing-paths'
import { FeatureFlagKeyOfType, LaunchDarklyFlagValue, LaunchDarklyService } from '@ftr/ui-feature-flags'
import { AuthenticationService } from '@ftr/ui-user'

/**
 * Create a route guard that checks for a specific feature flag value before allowing access to a route.
 * @param flagName Feature flag name
 * @param requiredValue - If not provided, the guard will check if the flag value is `true`. If provided, it will check if the flag value matches the required value.
 */
export function makeFeatureFlagGuard<T extends LaunchDarklyFlagValue = boolean>(
  flagName: FeatureFlagKeyOfType<T>,
  requiredValue?: T,
): CanActivateFn {
  return async route => {
    const launchDarklyService = inject(LaunchDarklyService)
    const authService = inject(AuthenticationService)
    const router = inject(Router)

    const courtSystemId = route.params[CourtSystemParams.CourtSystemId]

    const featureFlagValue = await launchDarklyService.getFlagVariationForUser(
      authService.currentUser!,
      flagName,
      courtSystemId,
    )
    const matchesRequiredValue =
      requiredValue !== undefined ? featureFlagValue === requiredValue : featureFlagValue === true
    if (!matchesRequiredValue) {
      return router.createUrlTree(['../'])
    }
    return true
  }
}

export function makePublicFeatureFlagGuard(flagName: FeatureFlagKeyOfType<boolean>): CanActivateFn {
  return async route => {
    const launchDarklyService = inject(LaunchDarklyService)
    const router = inject(Router)

    const courtSystemId = route.params[CourtSystemParams.CourtSystemId]

    const authService = inject(AuthenticationService)
    const isLoggedIn = await authService.isLoggedIn
    let featureFlagEnabled

    if (isLoggedIn && authService.currentUser) {
      featureFlagEnabled = await launchDarklyService.getFlagVariationForUser(
        authService.currentUser!,
        flagName,
        courtSystemId,
      )
    } else {
      featureFlagEnabled = await launchDarklyService.getFlagVariationForAnonymous(flagName, courtSystemId)
    }
    if (!featureFlagEnabled) {
      return router.createUrlTree(['../'])
    }
    return true
  }
}
